import React, { Component } from 'react';
import cn from 'classnames';
import _ from 'lodash';
import Modal from './modal';
import MusicPlayer from './music-player';
import ScrollBars from 'react-scrollbar';
import { getCachedImageUrl } from '../services/image-cache';
import { infoCardIsLockedByID } from '../user';
import { withRouter } from 'react-router-dom';

/**
 * LinkedTrackModal - Displays alternate versions of a performance
 * 
 * Two-column layout:
 * - Left: Song cards with play button, image, title, date
 * - Right: Albums where each track appears
 */

// Song item component (left column)
class LinkedTrackSongItemBase extends Component {
  constructor(props) {
    super(props);
    this.onCardClick = this.onCardClick.bind(this);
    this.onPlayClick = this.onPlayClick.bind(this);
  }

  onCardClick(e) {
    e.preventDefault();
    
    const { track, history, onClose } = this.props;
    
    if (track.listOnly || track.hiddenTrack) return;
    
    onClose();
    history.push(`/info-card?track=${track.id || track.trackId}`);
  }

  onPlayClick(e) {
    e.stopPropagation();
  }

  formatDate(releaseDate) {
    if (!releaseDate) return '';
    const { month, day, year } = releaseDate;
    return `${month}/${day}/${year}`;
  }

  getAlbumImage(track) {
    if (track.searchImage) return track.searchImage;
    if (track.infoCardAlbumImage) return track.infoCardAlbumImage;
    if (track.playerImage) return track.playerImage;
    
    if (track.album && track.album.infoCardImage) {
      return track.album.infoCardImage;
    }
    
    if (track.presentOn) {
      const albums = Object.values(track.presentOn).filter(a => a && a.infoCardImage);
      if (albums.length > 0) {
        return albums[0].infoCardImage;
      }
    }
    
    return null;
  }

  render() {
    const { track } = this.props;
    
    const hasInfoCard = !(track.listOnly || track.hiddenTrack);
    const disabled = infoCardIsLockedByID(track.id || track.trackId);
    const recordingDate = this.formatDate(track.releaseDate);
    const albumImage = this.getAlbumImage(track);

    return (
      <div 
        className={cn('linked-track-song-item', { 'linked-track-song-item--clickable': hasInfoCard })}
        onClick={hasInfoCard ? this.onCardClick : undefined}
      >
        {/* Play Button */}
        <div className="linked-track-play" onClick={this.onPlayClick}>
          <MusicPlayer
            item={track}
            scaled={true}
            disabled={disabled}
            makeStateInSync
            dummy
          />
        </div>
<div className='linked-track-wrapper'>
        {/* Album Image */}
        {albumImage ? (
          <img 
            src={getCachedImageUrl(albumImage)} 
            alt={track.title}
            className="linked-track-image"
            loading="lazy"
          />
        ) : (
          <div className="linked-track-image linked-track-image--placeholder" />
        )}
        
        {/* Track Description */}
        <div className="linked-track-description">
          <div className="linked-track-title">{track.title}</div>
          <div className="linked-track-artist">
            {track.performer || 'Neil Young'}, {recordingDate}
          </div>
        </div>
      </div>
      </div>
    );
  }
}

const LinkedTrackSongItem = withRouter(LinkedTrackSongItemBase);

// Albums item component (right column)
class LinkedTrackAlbumItemsBase extends Component {
  constructor(props) {
    super(props);
    this.onAlbumClick = this.onAlbumClick.bind(this);
  }

  onAlbumClick(e, albumId) {
    e.preventDefault();
    e.stopPropagation();
    
    const { history, onClose } = this.props;
    
    onClose();
    history.push(`/album?id=${albumId}`);
  }

  getAlbums(track) {
    if (!track.presentOn) return [];
    return Object.keys(track.presentOn)
      .map(k => track.presentOn[k])
      .filter(album => album && (album.type === 'album' || album.isAlbum || album.infoCardImage));
  }

  render() {
    const { track } = this.props;
    const albums = this.getAlbums(track);

    if (albums.length === 0) return <div className="linked-track-albums-row" />;

    return (
      <div className="linked-track-albums-row">
        {albums.map((album, idx) => (
          <div 
            key={album.id || album.albumId || idx} 
            className="linked-track-album-item"
            onClick={(e) => this.onAlbumClick(e, album.id || album.albumId)}
            title={album.title}
          >
            {album.infoCardImage && (
              <img 
                src={getCachedImageUrl(album.infoCardImage)} 
                alt={album.title}
                className="linked-track-album-cover"
                loading="lazy"
              />
            )}
            <div className="linked-track-album-info">
              <span className="linked-track-album-title">{album.title}</span>
              {(album.displayDate || album.releaseDate) && (
                <span className="linked-track-album-year">
                  {album.displayDate?.year || album.releaseDate?.year}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }
}

const LinkedTrackAlbumItems = withRouter(LinkedTrackAlbumItemsBase);

class LinkedTrackModal extends Component {
  constructor(props) {
    super(props);
    this.onClose = this.onClose.bind(this);
    this.onBGClick = this.onBGClick.bind(this);
  }

  onClose() {
    if (this.props.onClose) {
      this.props.onClose();
    }
  }

  onBGClick(e) {
    // Close when clicking on background
    if (e.target.classList.contains('linked-track-modal-inner')) {
      this.onClose();
    }
  }

  render() {
    const { isOpen, linkedTrack, sourceTrack } = this.props;
    
    if (!linkedTrack || linkedTrack.length === 0) {
      return null;
    }

    // Sort by recording date (oldest first)
    const sortedTracks = [...linkedTrack].sort((a, b) => {
      if (!a.releaseDate || !b.releaseDate) return 0;
      if (a.releaseDate.year !== b.releaseDate.year) {
        return a.releaseDate.year - b.releaseDate.year;
      }
      if (a.releaseDate.month !== b.releaseDate.month) {
        return a.releaseDate.month - b.releaseDate.month;
      }
      return a.releaseDate.day - b.releaseDate.day;
    });

    const totalResults = sortedTracks.length;

    return (
      <Modal 
        isOpen={isOpen} 
        onRequestClose={this.onClose}
        className="linked-track-modal"
        overlayClassName="linked-track-modal-overlay"
        portalClassName="linked-track-modal-portal"
      >
        <div className="linked-track-modal-inner" onClick={this.onBGClick}>
          <div className="linked-track-content">
            {/* Close button */}
            <div className="linked-track-close-button" onClick={this.onClose} />
            
            {/* Background */}
            <div className="linked-track-background" />

            {/* Header */}
            <div className="linked-track-header">
              <div className="linked-track-header-title">Alternate Versions</div>
              {sourceTrack && (
                <div className="linked-track-header-source">
                  of "{sourceTrack.title}"
                </div>
              )}
            </div>

            {/* Results area */}
            <div className="linked-track-results-area">
              {/* Column headers - SONGS | ALBUMS */}
              <div className="linked-track-tabs">
                <div className="linked-track-tab linked-track-tab--songs">SONGS</div>
                <div className="linked-track-tab linked-track-tab--albums">ALBUMS</div>
              </div>

              {/* Track rows - each row has song + albums with single line underneath */}
              <div className="linked-track-rows-wrapper">
                <ScrollBars>
                  <div className="linked-track-rows">
                    {sortedTracks.map((track, index) => (
                      <div key={track.id || track.trackId || index} className="linked-track-row">
                        <div className="linked-track-row-content">
                          {/* Song (left) */}
                          <div className="linked-track-song-cell">
                            <LinkedTrackSongItem
                              track={track}
                              index={index}
                              onClose={this.onClose}
                            />
                          </div>
                          {/* Albums (right) */}
                          <div className="linked-track-albums-cell">
                            <LinkedTrackAlbumItems
                              track={track}
                              onClose={this.onClose}
                            />
                          </div>
                        </div>
                        <div className="linked-track-row-separator" />
                      </div>
                    ))}
                  </div>
                </ScrollBars>
              </div>
            </div>

            {/* Bottom line */}
            <div className="linked-track-bottom-line" />
          </div>
        </div>
      </Modal>
    );
  }
}

export default LinkedTrackModal;
